// src/components/PlayersSection.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Link } from "react-router-dom";
import styles from "../styles/FixturePage.module.css";
import { api } from "../api";

const fmtPct = (p) =>
  p == null ? "—" : `${(Number(p) * 100).toFixed(0)}%`;
const fmtOdds = (o) =>
  o == null
    ? "—"
    : Number(o) >= 100
    ? Number(o).toFixed(0)
    : Number(o).toFixed(2);
const fmtEdgeVal = (edge, p, price) =>
  edge != null
    ? `${(Number(edge) * 100).toFixed(1)}%`
    : p && price
    ? `${(((Number(p) * Number(price)) - 1) * 100).toFixed(1)}%`
    : "—";

export default function PlayersSection({ fixtureId, homeTeam, awayTeam }) {
  const [seasonData, setSeasonData] = useState({ home: [], away: [] });
  const [lineupIds, setLineupIds] = useState({ home: null, away: null });
  const [propsMap, setPropsMap] = useState({});
  const [activeTeam, setActiveTeam] = useState("home");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function fetchAll() {
      try {
        setLoading(true);

        // 1) Season totals (cached)
        const seasonRes = await api.get("/football/season-players", {
          params: { fixture_id: fixtureId },
        });
        const seasonJson = seasonRes.data;
        const seasonPlayers = seasonJson?.players || { home: [], away: [] };

        // 2) Lineups – only used to filter down the season list
        const lineRes = await api.get("/football/lineups", {
          params: { fixture_id: fixtureId },
        });
        const lineJson = lineRes.data;

        if (!mounted) return;

        setSeasonData({
          home: seasonPlayers.home || [],
          away: seasonPlayers.away || [],
        });

        const toIdSet = (teamBlock) => {
          if (!teamBlock) return null;
          const ids = new Set();
          (teamBlock.startXI || []).forEach(
            (x) => x?.player?.id && ids.add(x.player.id)
          );
          (teamBlock.substitutes || []).forEach(
            (x) => x?.player?.id && ids.add(x.player.id)
          );
          return ids;
        };

        let homeLine = null,
          awayLine = null;
        const arr = Array.isArray(lineJson?.response)
          ? lineJson.response
          : [];
        for (const t of arr) {
          const tname = t?.team?.name?.toLowerCase?.() || "";
          if (tname === (homeTeam || "").toLowerCase()) homeLine = t;
          if (tname === (awayTeam || "").toLowerCase()) awayLine = t;
        }
        setLineupIds({ home: toIdSet(homeLine), away: toIdSet(awayLine) });

        // 3) Player props (shots / SoT / fouls / cards)
        const [homePropsRes, awayPropsRes] = await Promise.all([
          api.get("/football/player-props/fair", {
            params: { fixture_id: fixtureId, team: "home" },
          }),
          api.get("/football/player-props/fair", {
            params: { fixture_id: fixtureId, team: "away" },
          }),
        ]);
        const homeProps = homePropsRes.data;
        const awayProps = awayPropsRes.data;

        const buildMap = (propsObj) => {
          const map = {};
          (propsObj?.props || []).forEach((r) => {
            const pid = Number(r.player_id);
            if (!map[pid]) map[pid] = {};
            map[pid][r.market] = r;
          });
          return map;
        };

        if (!mounted) return;
        setPropsMap({ ...buildMap(homeProps), ...buildMap(awayProps) });
      } catch (err) {
        console.error("PlayersSection fetch error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchAll();
    return () => {
      mounted = false;
    };
  }, [fixtureId, homeTeam, awayTeam]);

  // ✅ Season-only merge (optionally filtered to lineup)
  const mergeSeason = useCallback(
    (seasonList, idSet) => {
      const filtered = idSet
        ? seasonList.filter((p) => idSet.has(p?.player?.id))
        : seasonList;

      const n = (x) => Number(x || 0);

      return filtered.map((sp) => {
        const pid = sp?.player?.id;
        const competitions = sp.competitions || [];

        // try direct totals first
        let seasonTotal = sp.total || null;

        // 🔥 fallback: compute season totals by summing competitions
        if (!seasonTotal && competitions.length) {
          const apps = competitions.reduce(
            (sum, c) =>
              sum +
              n(
                c?.games?.appearences ??
                  c?.games?.appearances ??
                  0
              ),
            0
          );
          const minutes = competitions.reduce(
            (sum, c) => sum + n(c?.games?.minutes),
            0
          );
          const goals = competitions.reduce(
            (sum, c) => sum + n(c?.goals?.total),
            0
          );
          const assists = competitions.reduce(
            (sum, c) => sum + n(c?.assists),
            0
          );
          const shots = competitions.reduce(
            (sum, c) => sum + n(c?.shots?.total),
            0
          );
          const shots_on = competitions.reduce(
            (sum, c) => sum + n(c?.shots?.on),
            0
          );
          const yellow = competitions.reduce(
            (sum, c) => sum + n(c?.cards?.yellow),
            0
          );
          const red = competitions.reduce(
            (sum, c) => sum + n(c?.cards?.red),
            0
          );

          seasonTotal = {
            apps,
            minutes,
            goals,
            assists,
            shots,
            shots_on,
            yellow,
            red,
          };
        }

        return {
          ...sp,
          seasonTotal,
          competitions,
          props: propsMap[pid] || {},
        };
      });
    },
    [propsMap]
  );

  const homePlayers = useMemo(
    () => mergeSeason(seasonData.home, lineupIds.home),
    [seasonData, lineupIds, mergeSeason]
  );

  const awayPlayers = useMemo(
    () => mergeSeason(seasonData.away, lineupIds.away),
    [seasonData, lineupIds, mergeSeason]
  );

  const renderPropsRow = (p) => {
    const pr = p.props || {};
    const shots = pr["shots_over_1.5"];
    const sot = pr["sot_over_0.5"];
    const fouls = pr["fouls_over_0.5"];
    const card = pr["to_be_booked"];

    const cell = (label, r) =>
      r ? (
        <span>
          {label}: {fmtPct(r?.prob)} (fair {fmtOdds(r?.fair_odds)}){" "}
          {r?.best_price ? `@${fmtOdds(r.best_price)}` : ""}{" "}
          {r ? `· ${fmtEdgeVal(r.edge, r.prob, r.best_price)}` : ""}
        </span>
      ) : null;

    return (
      <div className={styles.playerPropsLine}>
        {cell("Sh 1.5", shots)}
        {cell("SoT 0.5", sot)}
        {cell("Fouls 0.5", fouls)}
        {cell("Card", card)}
      </div>
    );
  };

  const renderTeam = (label, list) => {
    if (!list.length) {
      return (
        <p style={{ fontSize: 13, color: "#000" }}>
          No season stats found for {label}.
        </p>
      );
    }

    const getMaxEdge = (pl) =>
      Math.max(
        pl?.props?.["sot_over_0.5"]?.edge ?? -1,
        pl?.props?.["shots_over_1.5"]?.edge ?? -1,
        pl?.props?.["fouls_over_0.5"]?.edge ?? -1,
        pl?.props?.["to_be_booked"]?.edge ?? -1
      );

    const scored = [...list].sort((a, b) => {
      const ea = getMaxEdge(a);
      const eb = getMaxEdge(b);
      if (eb !== ea) return eb - ea;
      const sa = a?.props?.["sot_over_0.5"]?.prob ?? 0;
      const sb = b?.props?.["sot_over_0.5"]?.prob ?? 0;
      return sb - sa;
    });

    return (
      <div className={styles.playersPanel}>
        <ul className={styles.playerList}>
          {scored.map((p, i) => {
            const mainComp = p.competitions?.[0] || {};
            const games = mainComp.games || {};
            const pos = games.position || p.player?.position || "?";

            const season = p.seasonTotal || {};
            const appsSeason = season.apps ?? 0;
            const minsSeason = season.minutes ?? 0;
            const goalsSeason = season.goals ?? 0;
            const assistsSeason = season.assists ?? 0;
            const shotsSeason = season.shots ?? 0;
            const shotsOnSeason = season.shots_on ?? 0;
            const ycSeason = season.yellow ?? 0;
            const rcSeason = season.red ?? 0;

            return (
              <li
                key={p.player?.id ?? i}
                className={styles.playerRow}
              >
                <img
                  src={p.player?.photo}
                  alt={p.player?.name}
                  className={styles.playerPhotoTiny}
                  onError={(e) =>
                    (e.currentTarget.style.display = "none")
                  }
                />
                <div className={styles.playerMeta}>
                  <div className={styles.playerNameLine}>
                    <strong>
                      <Link
                        to={`/player/${p.player?.id}?fixture_id=${fixtureId}`}
                      >
                        {p.player?.name}
                      </Link>
                    </strong>
                    <span className={styles.playerPos}>
                      {" "}
                      ({pos})
                    </span>
                  </div>

                  {/* full season snapshot */}
                  <div className={styles.playerStatsGrid}>
                    <span><b>{goalsSeason}</b> Goals</span>
                    <span><b>{assistsSeason}</b> Assists</span>
                    <span><b>{shotsSeason}</b> Shots</span>
                    <span><b>{shotsOnSeason}</b> On target</span>
                    <span className={styles.cardStat}><b>{ycSeason}</b> Yellow</span>
                    <span className={styles.dangerStat}><b>{rcSeason}</b> Red</span>
                  </div>

                  {/* season totals line */}
                  {p.seasonTotal && (
                    <div
                      className={styles.playerSubline}
                    >
                      Season: {appsSeason} apps, {goalsSeason} goals,{" "}
                      {assistsSeason} assists, {minsSeason} min
                    </div>
                  )}

                  {renderPropsRow(p)}

                  {p.competitions?.length > 0 && (
                    <details className={styles.playerBreakdown}>
                      <summary>
                        Show competition breakdown
                      </summary>
                      <table className={styles.oddsTable}>
                        <thead>
                          <tr>
                            <th>Competition</th>
                            <th>Apps</th>
                            <th>Goals</th>
                            <th>Assists</th>
                            <th>Minutes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.competitions.map((c, j) => (
                            <tr key={j}>
                              <td>{c.league}</td>
                              <td>
                                {c?.games?.appearences ??
                                  c?.games?.appearances ??
                                  0}
                              </td>
                              <td>{c?.goals?.total ?? 0}</td>
                              <td>{c?.assists ?? 0}</td>
                              <td>{c?.games?.minutes ?? 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  // 🔄 Loading state vs empty state
  if (loading) {
    return (
      <div className={styles.tabContent}>
        <p style={{ fontSize: 13, color: "#000" }}>
          Loading player stats…
        </p>
      </div>
    );
  }

  if (!homePlayers.length && !awayPlayers.length) {
    return (
      <div className={styles.tabContent}>
        <p style={{ fontSize: 13, color: "#000" }}>
          No player stats available for this fixture.
        </p>
      </div>
    );
  }

  const activePlayers =
    activeTeam === "home" ? homePlayers : awayPlayers;
  const activeLabel = activeTeam === "home" ? homeTeam : awayTeam;

  return (
    <div className={styles.tabContent}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <h3 style={{ margin: 0 }}>Player Stats</h3>
        <div
          style={{
            marginLeft: "auto",
            display: "inline-flex",
            borderRadius: 999,
            padding: 2,
            background: "#fff",
            border: "1px solid #ddd",
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTeam("home")}
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              border: "none",
              fontSize: 12,
              cursor: "pointer",
              background:
                activeTeam === "home"
                  ? "rgba(34,197,94,0.9)"
                  : "transparent",
              color:
                activeTeam === "home" ? "#fff" : "#000",
            }}
          >
            {homeTeam}
          </button>
          <button
            type="button"
            onClick={() => setActiveTeam("away")}
            style={{
              padding: "4px 10px",
              borderRadius: 999,
              border: "none",
              fontSize: 12,
              cursor: "pointer",
              background:
                activeTeam === "away"
                  ? "rgba(34,197,94,0.9)"
                  : "transparent",
              color:
                activeTeam === "away" ? "#fff" : "#000",
            }}
          >
            {awayTeam}
          </button>
        </div>
      </div>

      <p style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
        Showing props and <strong>full season stats</strong> for{" "}
        <strong>{activeLabel}</strong>. Tap a player for their full
        dashboard.
      </p>

      {renderTeam(activeLabel, activePlayers)}
    </div>
  );
}
